import useIsTouchDevice from '@utils/hooks/useIsTouchDevice';
import classNames from 'classnames';
import { useReducedMotion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import {
  ACESFilmicToneMapping,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  PMREMGenerator,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

interface Props {
  readonly modelUrl?: string;
  readonly className?: string;
  readonly onModelLoading: (isLoading: boolean) => void;
  readonly shouldAnimate?: boolean;
}

const WebGlModelViewer = ({
  modelUrl,
  className,
  shouldAnimate,
  onModelLoading,
}: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<WebGLRenderer | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const sceneRef = useRef<Scene | null>(null);
  const cameraRef = useRef<PerspectiveCamera | null>(null);
  const modelRootRef = useRef<Object3D | null>(null);
  const rafRef = useRef<number | null>(null);
  const isTouchDevice = useIsTouchDevice();
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new Scene();
    const renderer = new WebGLRenderer({ antialias: true, alpha: true });
    renderer.toneMapping = ACESFilmicToneMapping;
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(renderer.domElement);
    renderer.domElement.classList.add('!touch-pan-y'); // for mobile touch behavior
    rendererRef.current = renderer;

    const environment = new RoomEnvironment();
    const pmremGenerator = new PMREMGenerator(renderer);
    scene.environment = pmremGenerator.fromScene(environment).texture;
    sceneRef.current = scene;

    const camera = new PerspectiveCamera(
      50,
      container.clientWidth / container.clientHeight,
      0.1,
      1000
    );
    camera.position.set(0, 0, 0.25);
    cameraRef.current = camera;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableRotate = true;
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enablePan = false;
    controls.screenSpacePanning = false;
    controls.minDistance = 0.18;
    controls.maxDistance = 0.3;
    controlsRef.current = controls;

    return () => {
      controlsRef.current?.dispose();
      rendererRef.current?.dispose();
      // dispose geometries & materials
      sceneRef.current?.traverse((obj) => {
        // @ts-ignore
        if (obj.geometry) obj.geometry.dispose();
        // @ts-ignore
        if (obj.material) {
          // @ts-ignore
          // biome-ignore lint/suspicious/noExplicitAny: Required by 3D lib
          const mat = obj.material as any;
          if (Array.isArray(mat)) mat.forEach((m) => m.dispose && m.dispose());
          else mat.dispose && mat.dispose();
        }
      });

      if (
        rendererRef.current?.domElement &&
        rendererRef.current.domElement.parentNode === containerRef.current
      ) {
        containerRef.current?.removeChild(renderer.domElement);
      }
    };
  }, []);

  useEffect(() => {
    if (controlsRef.current && isTouchDevice !== undefined) {
      controlsRef.current.enableZoom = !!isTouchDevice;
    }
  }, [isTouchDevice]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    if (!modelUrl) return;

    const loader = new GLTFLoader();
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
    loader.setDRACOLoader(dracoLoader);
    onModelLoading(true);
    loader.load(
      modelUrl,
      (gltf) => {
        modelRootRef.current = gltf.scene || gltf.scenes[0];
        modelRootRef.current.traverse((child) => {
          if ((child as Mesh).isMesh) {
            const mesh = child as Mesh;
            mesh.castShadow = true;
            mesh.receiveShadow = true;
            if (mesh.material) {
              const material = mesh.material as MeshStandardMaterial;
              if (material.transparent !== undefined) {
                // Start with model invisible for fade-in effect
                material.transparent = true;
                material.opacity = 0;

                // avoid the "S" part to disappear on certain angles
                if (mesh.name === 'Silver_Lens_Grp001') {
                  mesh.renderOrder = 1;
                }
              }

              // increase roughness of lens materials to make it look more mat
              if (material && mesh.name.includes('Lens')) {
                material.roughness = 1.75;
              }
            }
          }
        });

        sceneRef.current?.add(modelRootRef.current);
        setIsModelLoaded(true);
        onModelLoading(false);
      },
      // called while loading is progressing
      undefined,
      (error) => {
        // biome-ignore lint/suspicious/noConsole: Allowed during error reporting
        console.error('Error loading GLTF model:', error);
        onModelLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const container = containerRef.current;
      if (!container) return;

      const clientWidth = container.clientWidth;
      const clientHeight = container.clientHeight;
      rendererRef.current?.setSize(clientWidth, clientHeight);

      if (cameraRef.current) {
        cameraRef.current.aspect = clientWidth / clientHeight;
        cameraRef.current.updateProjectionMatrix();
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    if (!isModelLoaded) return;

    // Animation loop
    const animate = () => {
      if (modelRootRef.current) {
        modelRootRef.current.rotation.y +=
          !shouldReduceMotion && shouldAnimate ? 0.002 : 0; // slow rotation
        // Handle fade-in animation
        modelRootRef.current.traverse((child) => {
          if ((child as Mesh).isMesh) {
            const mesh = child as Mesh;
            if (mesh.material) {
              const material = mesh.material as MeshStandardMaterial;
              if (material.transparent && material.opacity < 1) {
                material.opacity = Math.min(1, material.opacity + 0.02); // fade in speed
              }
            }
          }
        });
      }

      controlsRef.current?.update();
      if (sceneRef.current && cameraRef.current)
        rendererRef.current?.render(sceneRef.current, cameraRef.current);
      rafRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isModelLoaded, shouldAnimate]);

  return (
    <section className={classNames('aspect-square w-full', className)}>
      <div ref={containerRef} className="h-full w-full" />
    </section>
  );
};

export default WebGlModelViewer;
